import { Injectable } from '@angular/core';

/** OAuth settings for the XPO API gateway (Apigee). */
export interface AuthConfig {
  /** When false (local dev via proxy) no token is fetched and no Bearer header is attached. */
  enabled: boolean;
  /**
   * Token endpoint. Either gateway host works (the tctsX-api-cust duplicated-ACAO CORS
   * defect is fixed as of 2026-09); TokenService falls back once to the twin host
   * (tctsX-api ↔ tctsX-api-cust) when this one is unreachable or 5xx.
   */
  tokenUrl: string;
  /** Optional explicit fallback token endpoint; defaults to the twin gateway host. */
  tokenFallbackUrl?: string;
  /**
   * OAuth grant. 'password' (dashboard sign-in): the login screen collects username/
   * password and the gateway validates them — credentials are never stored in config.
   * 'client_credentials': machine identity, tokens minted on demand from key/secret.
   */
  grantType?: 'client_credentials' | 'password';
  /** OAuth consumer key — Basic auth is computed at runtime as base64(key:secret). */
  key: string;
  /** OAuth consumer secret. */
  secret: string;
  /** Seconds before expiry at which the token is considered stale (proactive renewal). */
  refreshMarginSeconds?: number;
}

/**
 * User-facing auth mode:
 *  - 'none': no sign-in (local dev, or machine client_credentials — no user identity).
 *  - 'password': legacy gateway sign-in form (rollback path).
 *  - 'sso': XPO corporate SSO via @xpo-ltl/ngx-auth (jwt-bearer exchange at the gateway).
 */
export type AuthMode = 'none' | 'password' | 'sso';

export interface AppConfig {
  /**
   * Backend base URL.
   *  - Local dev: '/api' (Angular proxy → Spring Boot :8080, context-path /api).
   *  - Published: the gateway route, e.g. https://tctsi-api.ltl.xpo.com/webanalytics/1.0
   *    (NO '/api' suffix — Octopus overrides the pod context-path).
   */
  apiBaseUrl: string;
  /**
   * Chrome address space of the API host ('private' for the internal empl gateway).
   * When set, load() fires a probe fetch declaring targetAddressSpace so Chrome shows
   * its Local Network Access permission prompt instead of silently blocking every call
   * (public site → private IP enforcement, rolled out in 2026 Chrome versions).
   */
  apiAddressSpace?: 'public' | 'private' | 'local';
  authMode?: AuthMode;
  auth: AuthConfig;

  /*
   * Flat XPO SSO fields — XpoLtlAuthConfigLoaderService reads them itself straight from
   * config.json, so they must stay at the ROOT of the JSON (Angular SSO guide, Confluence
   * LA/35356716 + LA/35357103). Values come from the SSO registration ticket (LIDO).
   */
  apiUrl?: string;
  authVersion?: string;
  authAppCode?: string;
  authAppRootUri?: string;
  authIsProd?: boolean;
  authEnvironment?: string;
  authScopes?: string[];
  authGrantType?: string;
  appName?: string;
  region?: string;
}

const DEFAULTS: AppConfig = {
  apiBaseUrl: '/api',
  authMode: undefined,
  auth: {
    enabled: false, tokenUrl: '', grantType: 'password',
    key: '', secret: '',
    refreshMarginSeconds: 60,
  },
};

/**
 * Per-tab opt-in to SSO from the password login screen ("Sign in with XPO SSO").
 * sessionStorage so the choice survives the full-page reload the switch requires
 * but never leaks to other tabs or sessions; cleared on logout / ?mode=password.
 */
export const SSO_OVERRIDE_KEY = 'wa.auth-mode-override';

/**
 * Pre-bootstrap decision used by main.ts: XpoLtlAuthModule (SSO stack) transitively
 * installs ngx-auth's global interceptor, which queues EVERY HttpClient request until
 * a token exists — in none/password mode that token never arrives and all calls hang
 * forever. So the SSO stack may only be loaded when the tab will actually run in sso
 * mode; this mirrors AppConfigService.authMode without needing the injector.
 */
export function bootsInSsoMode(cfg: Partial<AppConfig> | null): boolean {
  if (!cfg) { return false; }
  const ssoConfigured = !!(cfg.authAppCode && cfg.appName && cfg.region);
  if (ssoConfigured && sessionStorage.getItem(SSO_OVERRIDE_KEY) === 'sso') { return true; }
  return cfg.authMode === 'sso';
}

/**
 * Runtime configuration (XPO multi-environment model): one build, per-environment values.
 * Loaded from /assets/config.json BEFORE the app starts (APP_INITIALIZER in AppModule).
 * `./assets/config.json` is the file the XPO Octopus Angular template already targets for
 * variable substitution at deploy time (tctsi / tctss / prod) — see
 * docs/runtime-config-octopus.md. Uses native fetch (not HttpClient) so the load can never
 * recurse through the auth interceptor.
 */
@Injectable({ providedIn: 'root' })
export class AppConfigService {

  private cfg: AppConfig = DEFAULTS;
  private loadedFrom = 'assets/config.json';

  get config(): AppConfig { return this.cfg; }
  get apiBaseUrl(): string { return this.cfg.apiBaseUrl; }
  get auth(): AuthConfig { return this.cfg.auth; }

  /** Path of the config file that actually loaded (config.local.json wins locally). */
  get configPath(): string { return `./${this.loadedFrom}`; }

  get authMode(): AuthMode {
    if (this.ssoConfigured && sessionStorage.getItem(SSO_OVERRIDE_KEY) === 'sso') { return 'sso'; }
    if (this.cfg.authMode) { return this.cfg.authMode; }
    // Legacy configs (no authMode key): only an enabled password grant shows a sign-in;
    // client_credentials is a machine identity, no user-facing auth.
    if (!this.cfg.auth.enabled) { return 'none'; }
    return (this.cfg.auth.grantType ?? 'client_credentials') === 'password' ? 'password' : 'none';
  }

  /** True when the flat SSO fields needed by @xpo-ltl/ngx-auth are present. */
  get ssoConfigured(): boolean {
    return !!(this.cfg.authAppCode && this.cfg.appName && this.cfg.region);
  }

  enableSsoOverride(): void { sessionStorage.setItem(SSO_OVERRIDE_KEY, 'sso'); }

  clearSsoOverride(): void { sessionStorage.removeItem(SSO_OVERRIDE_KEY); }

  /** Called once by APP_INITIALIZER. Falls back to safe local defaults if the file is missing. */
  async load(): Promise<void> {
    // Optional developer override (gitignored) wins over the deployable config.
    let loaded = await this.tryFetch('assets/config.local.json');
    if (loaded) {
      this.loadedFrom = 'assets/config.local.json';
    } else {
      loaded = await this.tryFetch('assets/config.json');
    }
    if (loaded) {
      this.cfg = {
        ...DEFAULTS,
        ...loaded,
        auth: { ...DEFAULTS.auth, ...(loaded.auth ?? {}) },
      };
    }
    this.requestLocalNetworkAccess();
  }

  /**
   * Fires one throwaway fetch declaring the API's address space so Chrome raises its
   * Local Network Access permission prompt up front (the grant is per site, so every
   * later XHR rides on it). Any response — including 401 — completes the negotiation;
   * a denial leaves the app exactly as blocked as it was, so errors are ignored.
   */
  private requestLocalNetworkAccess(): void {
    const space = this.cfg.apiAddressSpace;
    if (!space || space === 'public') { return; }
    const init = { cache: 'no-store', targetAddressSpace: space } as RequestInit;
    fetch(`${this.cfg.apiBaseUrl}/health-check`, init).catch(() => undefined);
  }

  private async tryFetch(relPath: string): Promise<AppConfig | null> {
    try {
      // Resolve against <base href> so deep links (/user-activity) don't break the lookup.
      const url = new URL(relPath, document.baseURI).toString();
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) { return null; }
      return await res.json() as AppConfig;
    } catch {
      return null;
    }
  }
}
